import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: var(--color-bg);
`;

const LoginCard = styled.div`
  background: var(--color-white);
  padding: 3rem;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  text-align: center;
  max-width: 400px;
  width: 100%;
`;

const Greeting = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
  font-family: var(--font-heading);
  color: var(--color-text);
`;

const Subtitle = styled.p`
  color: var(--color-text-light);
  margin-bottom: 2.5rem;
  font-size: 1.125rem;
  line-height: 1.6;
`;

const LoginButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  background: var(--color-white);
  color: var(--color-text);
  border: 2px solid var(--color-border);
  padding: 0.875rem 2rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  justify-content: center;

  &:hover {
    background: var(--color-bg);
    border-color: var(--color-text);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const BackLink = styled.a`
  display: inline-block;
  margin-top: 2rem;
  color: var(--color-text-light);
  text-decoration: none;
  font-size: 0.875rem;
  
  &:hover {
    color: var(--color-accent);
  }
`;

const Quote = styled.p`
  font-style: italic;
  color: var(--color-text-light);
  margin-top: 2.5rem;
  font-size: 0.95rem;
  line-height: 1.5;
`;

const SecureNote = styled.p`
  font-size: 0.75rem;
  color: var(--color-text-light);
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--color-border);
`;

const Login = () => {
  const navigate = useNavigate();
  const { currentUser, signInWithGoogle } = useAuth();

  useEffect(() => {
    // If already logged in, redirect to home
    if (currentUser) {
      navigate('/');
    }
  }, [currentUser, navigate]);

  const handleLogin = async () => {
    try {
      const user = await signInWithGoogle();
      if (user) {
        navigate('/');
      }
    } catch (error) {
      toast.error('Failed to sign in');
    }
  };

  return (
    <Container>
      <LoginCard>
        <Greeting>Welcome back!</Greeting>
        <Subtitle>
          Ready to share more thoughts on meaning, identity, and the beautiful chaos of existence?
        </Subtitle>
        
        <LoginButton onClick={handleLogin}>
          <GoogleIcon />
          Sign in with Google
        </LoginButton>
        
        <Quote>
          "Write like you're running out of time"
        </Quote>
        
        <SecureNote>
          🔒 Access restricted to authorized accounts only
        </SecureNote>
        
        <BackLink href="/">← Back to blog</BackLink>
      </LoginCard>
    </Container>
  );
};

export default Login;
